import { useEffect, useState } from 'react';
import { CustomDialogProps } from '@/dashboard/components/Dialog/index';
import LogoLoading from '@/dashboard/components/loading/LogoLoading';
import PolicyModel from '@/dashboard/models/policys/PolicyModel';
import { DatePickerControlled } from '@/dashboard/mui/form/DatePicker';
import { SelectControlled } from '@/dashboard/mui/form/Select';
import { XCircleIcon } from '@heroicons/react/outline';
import {
  PROVIDER_SOURCE,
  apiRequestCancelPolicyWithFileInfo,
  convertTimeToServerStoredTimeDataWithZeroTime,
  judgeWhetherIsBetweenTwoDate,
  useCancelPolicyRelatedInfo,
} from '@jove-frontend/uikit';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Typography } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { FormProvider, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

type CancelPolicyDialogFormViewProps = CustomDialogProps & {
  confirm?: (info: any) => void;
  policy: PolicyModel;
  submitInfo: {
    cancelDate: Dayjs;
    reason: string;
  };
  cancelSuccess?: () => void;
};

const CancelPolicyDialogFormView = (props: CancelPolicyDialogFormViewProps) => {
  const { close, confirm, policy, submitInfo: { cancelDate, reason } = {}, cancelSuccess } = props;
  const methods = useForm({
    defaultValues: {
      cancelDate: cancelDate || dayjs(new Date()),
      cancelReson: reason || '',
    },
  });
  const { register, getValues, handleSubmit, control, watch } = methods;
  const { cancelReasonList, cancelReasonListLoading, requestPolicyCancelReasonList } = useCancelPolicyRelatedInfo();
  const [loading, setLoading] = useState(false);

  /**
   * requests
   */
  const requestCancelPolicy = async () => {
    if ([PROVIDER_SOURCE().CHUBB.value].includes(policy.providerCode)) {
      const params = {
        cancelDate: getValues('cancelDate'),
        reason: reasonToCancel,
      };

      if (confirm) confirm(params);
    } else {
      const { cancelDate, cancelReson } = getValues();
      const params: any = {
        cancellation_date: convertTimeToServerStoredTimeDataWithZeroTime(cancelDate),
        policy_id: policy?.id,
      };
      if (cancelReson) {
        params.reason = cancelReson;
      }

      setLoading(true);
      const [res] = await apiRequestCancelPolicyWithFileInfo(params);
      setLoading(false);

      if (res) {
        toast.success('Cancel policy successfully!');
        cancelSuccess?.();
      }
    }
  };

  /**
   * watch
   */
  const reasonToCancel = watch('cancelReson');

  useEffect(() => {
    requestPolicyCancelReasonList();
  }, []);

  const closeAction = () => {
    if (close) close();
  };

  const checkFormSuccessCallback = () => {
    requestCancelPolicy();
  };

  const confirmAction = () => {
    handleSubmit(checkFormSuccessCallback)();
  };

  const cancelDateValidate = (time) => {
    return judgeWhetherIsBetweenTwoDate(time, dayjs(new Date()).startOf('day'), policy.policyEndDateDayjs) ? true : 'Please select a valid date';
  };

  return (
    <FormProvider {...methods}>
      <form className="relative">
        {(cancelReasonListLoading || loading) && <LogoLoading />}
        <Box className="mt-4 flex flex-col items-center">
          <Box className="bg-brand-secondary/20 text-brand-secondary rounded-full p-2">
            <XCircleIcon className="h-7 w-7" />
          </Box>
          <Typography className="text-black-secondary mt-4 text-center font-black" variant="h5">
            Cancel Business insurance
          </Typography>
          <Typography className="text-black-secondary mt-6 text-center font-bold" variant="subtitle1">
            What is your cancellation date?
          </Typography>
          <DatePickerControlled
            name="cancelDate"
            control={control}
            {...register('cancelDate', {
              required: true,
              validate: cancelDateValidate,
            })}
            minDate={dayjs(new Date())}
            maxDate={policy.policyEndDateDayjs}
            className="mt-5"
            label="Cancellation date"
          />
          <SelectControlled
            name="cancelReson"
            control={control}
            {...register('cancelReson')}
            className="mt-4 w-full"
            options={cancelReasonList}
            label="Select reason of cancellation"
            hideClose={true}
          />
          <Box className="mt-6 flex w-full flex-row justify-end">
            <Button className="text-brand-primary text-lg font-bold" variant="text" onClick={closeAction}>
              Stay protected
            </Button>
            <LoadingButton
              className="ml-2 h-12 w-[190px] rounded-full text-lg font-bold"
              loading={cancelReasonListLoading || loading}
              loadingIndicator="Loading..."
              color="primary"
              onClick={confirmAction}
              variant="contained"
              disabled={!reasonToCancel}
            >
              <span>Cancel insurance</span>
            </LoadingButton>
          </Box>
        </Box>
      </form>
    </FormProvider>
  );
};

export default CancelPolicyDialogFormView;
